import React from 'react';

import { Divider, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useGoalsCascadedViewData } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/dataHooks';
import { getGoalsPathnameToRedirectTo } from 'screens/Team/Rabbit/Goals/AgGridTreeView/hooks/useShowParentObjectivesPersistence';
import RabbitGoalProgressBar from 'screens/Team/Rabbit/Goals/Audit/RabbitGoalProgressBar';
import { setTimeBoundsToLS } from 'screens/Team/Rabbit/Goals/Header';
import { selectGoalAndMetadataFromGlobalGoals } from 'selectors/globalGoalSelector';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';

import { Goal, OpenSidePanel } from 'assets/assetsV1/iconComponents';
import NoDataImg from 'assets/assetsV1/illustrations/newIllustrations/noGoal.svg';

import { TASK_TYPES_2, TEAM_TYPE } from 'constants/ProjectConstants';
import { StringModifier, modifyString } from 'constants/copyHelpers';
import { sendPageViewEvent, sendOpenedKRPanelEvent } from 'constants/mixpanelHelper';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { showTaskDetails } from 'services/taskDetails';

import { Avatar } from 'uiToolkitV1/Avatar';
import { AvatarGroup } from 'uiToolkitV1/AvatarGroup';
import { Button } from 'uiToolkitV1/Button';
import { DateComp } from 'uiToolkitV1/DateComp';
import { SkeletonLoader } from 'uiToolkitV1/SkeletonLoader';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { EmptySummaryCard } from '../../EmptyCard/EmptySummaryCard';
import { MySummaryCardTemplate } from '../MySummaryCardTemplate';
import { GoalsMySummaryAgGrid } from './components/GoalsMySummaryAgGrid';
import { useTimeBoundTimePeriods } from './hooks';

const Container = styled(Flex)`
  padding: 12px 8px;
  &:hover {
    background: ${(props) => props.theme.colors.neutral.F6FAFE};
    & .highlightTextOnHover {
      color: #1565bc;
    }
  }
  cursor: pointer;
`;

const StyledAvatarGroup = styled(AvatarGroup)`
  display: flex;
  ${Container}:hover & {
    visibility: visible;
    display: none;
  }
`;
const ViewDetailsButton = styled(Button)`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;
const ButtonContainer = styled('div')`
  margin-left: auto;
  visibility: hidden;
  display: none;
  ${Container}:hover & {
    visibility: visible;
    display: flex;
  }
`;

const GoalsMySummaryRow = ({ goal: localGoal }) => {
  const dispatch = useDispatch();

  const globalGoalAndMetadata = useSelector(selectGoalAndMetadataFromGlobalGoals(localGoal?.id));

  if (globalGoalAndMetadata?.isDeleted) return null;

  const globalGoal = globalGoalAndMetadata?.data ?? localGoal;

  const { id, text: goalName, goalProgressLevel, dueDate, toIds: employees = [] } = globalGoal;

  const showDetails = ({ index, taskId, type, originalGoal }) => {
    sendOpenedKRPanelEvent({
      eventProperties: {
        source: 'Home Page',
      },
    });
    dispatch(
      showTaskDetails({
        taskIds: [taskId],
        onDelete: null,
        employee: {},
        type: TASK_TYPES_2.goal,
      })
    );
  };

  const DateText = ({ children }) => (
    <TextBodyMedium mt="2px" color="text.g2">
      {children}
    </TextBodyMedium>
  );

  const employeesCollaboratorsFirst = employees?.sort((a, b) => {
    if (a.ownerType < b.ownerType) {
      return 1;
    }
    if (a.ownerType > b.ownerType) {
      return -1;
    }
    return 0;
  });

  return (
    <>
      <Container
        onClick={() => {
          showDetails({ taskId: id });
        }}
      >
        <Flex width="100%">
          <Flex flexDir="column" width="100%">
            <TextBodyMedium className="highlightTextOnHover">{goalName}</TextBodyMedium>
            <Flex mt="20px" alignItems="center">
              <Flex flexDir="column" w="242px">
                <RabbitGoalProgressBar
                  goalProgressLevel={goalProgressLevel}
                  percentageValue={Number(globalGoal.getGoalProgress(0) || 0)}
                />
                {dueDate && (
                  <DateComp
                    date={dueDate}
                    isRelativeDateFormat
                    enableRelativeFormatDistanceColor
                    textComponent={DateText}
                    forceRelativeDateFormat
                    showDateInBrackets
                  />
                )}
              </Flex>
              <StyledAvatarGroup ml="auto">
                {employeesCollaboratorsFirst?.map(({ displayName, image, id, ownerType }) => (
                  <Avatar
                    sx={{ border: ownerType === 'OWNER' ? '1px solid #60C861 !important' : '0' }}
                    name={displayName}
                    src={image}
                    key={id}
                  />
                ))}
              </StyledAvatarGroup>
              <ButtonContainer className="showOnHover">
                <ViewDetailsButton
                  onClick={() => {
                    showDetails({ taskId: id });
                  }}
                  variant="rowButtonOutlined"
                  StartIcon={OpenSidePanel}
                >
                  View Details
                </ViewDetailsButton>
              </ButtonContainer>
            </Flex>
          </Flex>
        </Flex>
      </Container>
      <Divider color="neutral.CFD7DF" />
    </>
  );
};

const GROUP_BY = {
  uuid: null,
  keyName: 'objective',
  displayName: 'Objective',
  enabled: true,
};

const SPLIT_BY = {
  uuid: null,
  keyName: 'none',
  displayName: 'None',
  enabled: true,
};

const getSelectedCohort = (loggedInEmployee) => {
  const personalTeam = loggedInEmployee?.teams.find((team) => team.teamType === TEAM_TYPE.personal);

  return {
    id: personalTeam?.id,
    uuid: String(personalTeam?.id),
    createdTS: null,
    updatedTS: null,
    organization: null,
    employee: null,
    name: 'My Goals',
    query: {
      function: null,
      grade: null,
      gender: null,
      ethnicity: null,
      location: null,
      designations: null,
      provider: null,
      functionId: null,
      roleId: null,
      teamId: {
        id: String(personalTeam?.id),
        displayName: String(personalTeam?.id),
      },
      goals: null,
      engagement: null,
      review: null,
      oneOnOne: null,
      feedback: null,
      competency: null,
    },
    baseCohort: null,
    visibilityType: null,
    message: null,
    type: 'MY_OKRs',
    isPinned: null,
    pinnedAt: null,
  };
};

const GoalsMySummaryCard = ({ employeeId }) => {
  const { moduleName, objectivesName, keyResultsName } = useOrganisationSettings();

  const {
    user: { id },
  } = useAuthenticatedUser();

  const history = useHistory();

  const { data: timelineData, isLoading: timelineAPILoading } = useTimeBoundTimePeriods();

  const loggedInEmployee = useSelector(selectLoggedInEmployeeData);

  // const { data, isLoading, isError } = useMySummaryGoalsV1({
  //   limit: 3,
  //   offset: 0,
  //   enabled: Boolean(timelineData?.current),
  //   timeBounds: timelineData?.current ? [timelineData?.current] : null
  // });

  const sideNavSelectedCohort = getSelectedCohort(loggedInEmployee);

  const { data, isLoading } = useGoalsCascadedViewData({
    attributeFilter: {
      filterConditions: [
        {
          type: 'NAME',
          operatorType: 'IN',
          data: [loggedInEmployee?.id],
        },
        {
          type: 'TYPE',
          operatorType: 'IN',
          data: ['GOAL'],
        },
      ],
    },
    cohort: sideNavSelectedCohort,
    groupByKeys: GROUP_BY,
    splitByKeys: SPLIT_BY,
    orderBy: null,
    timeBounds: timelineData?.current ? [timelineData?.current] : [null],
    isHierarchyView: false,
    pageType: 'HOME_PAGE',
  });

  const queryClient = useQueryClient();

  const refetchCascadedViewData = () => {
    queryClient.invalidateQueries([REACT_QUERY_KEYS.GoalsCascadedData]);
  };

  if (isLoading || timelineAPILoading) {
    return (
      <MySummaryCardTemplate showViewAllButton={false} Icon={Goal} heading={moduleName}>
        <div style={{ marginTop: '16px' }}>
          <SkeletonLoader />
        </div>
      </MySummaryCardTemplate>
    );
  }
  const handleViewAllClick = () => {
    if (timelineData) {
      const { current } = timelineData;
      setTimeBoundsToLS([current]);
    }
    history.push({
      pathname: routes.goals.list,
      search: `?isHierarchyView=false`,
    });
  };

  const redirectHref = getGoalsPathnameToRedirectTo({
    cohort: {
      id: -12,
      type: 'MY_OKRs',
    },
  });

  if (!data?.length) {
    return (
      <EmptySummaryCard
        Icon={Goal}
        heading={moduleName}
        description={`See the status of your goals or check in here with just a click. Start your journey by creating your first ${modifyString(
          moduleName,
          StringModifier.SENTENCE_CASE
        )}`}
        illustrationSrc={NoDataImg}
        redirectUrl={redirectHref}
      />
    );
  }

  return (
    <MySummaryCardTemplate
      Icon={Goal}
      heading={moduleName}
      viewAllText={`View All ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)}`}
      viewAllHref={redirectHref}
      onClickViewAll={() => {
        sendPageViewEvent({
          eventProperties: {
            page: 'My Goals',
            type: 'Explore',
            cohort: 'My goals',
            source: 'My Summary',
          },
        });
      }}
    >
      {/* {data?.map((goal) => {
        return <GoalsMySummaryRow key={goal?.id} goal={goal} />;
      })} */}
      <GoalsMySummaryAgGrid
        refetchCascadedViewData={refetchCascadedViewData}
        data={data}
        selectedTimeline={timelineData?.current}
        // setSelectedFilters={setSelectedFilters}
        // selectedFilters={selectedFilters}
        // gridRefForParent={gridRef}
      />
    </MySummaryCardTemplate>
  );
};

export { GoalsMySummaryCard };
